import React from 'react';
import ContentSection from '../components/sections/ContentSection';
import FeatureSection from '../components/sections/FeatureSection';

const ProductsPage: React.FC = () => {
  // Features for capabilities section
  const capabilitiesFeatures = [
    {
      title: 'Intuitive On-boarding',
      description: 'Getting started with AiDA is straightforward and user-friendly, allowing for quick implementation without extensive technical expertise.'
    },
    {
      title: 'Seamless Integration',
      description: 'AiDA easily integrates with your existing systems through non-intrusive methods, minimizing disruption to your current operations.'
    },
    {
      title: 'Human-like Personalization',
      description: 'Every customer interaction is tailored to individual needs and preferences, creating a personalized experience that builds loyalty.'
    },
    {
      title: 'Continuous Self-learning',
      description: 'AiDA constantly improves its capabilities through ongoing learning, becoming more effective and efficient with each interaction.'
    },
    {
      title: 'AI-Powered Business Analytics',
      description: 'Beyond customer service, AiDA provides valuable insights into customer behavior and preferences for data-driven business decisions.'
    },
    {
      title: 'Agent-Human Collaboration',
      description: 'Rather than replacing human agents, AiDA works alongside them, handling routine inquiries while allowing your team to focus on complex issues.'
    }
  ];

  // Features for industry applications
  const industryFeatures = [
    {
      title: 'Retail',
      description: 'Enhance shopping experiences with personalized recommendations, inventory inquiries, and seamless order processing.'
    },
    {
      title: 'Travel',
      description: 'Simplify booking processes, provide travel information, and offer personalized itinerary suggestions.'
    },
    {
      title: 'Healthcare',
      description: 'Assist with appointment scheduling, basic health information, and patient support services.'
    },
    {
      title: 'Finance',
      description: 'Provide account information, transaction assistance, and basic financial guidance while maintaining strict security protocols.'
    }
  ];

  return (
    <>
      <div className="bg-gradient-to-r from-primary-800 to-secondary-800 text-white py-20 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-40 h-40 bg-accent-500 opacity-15 rounded-full blur-3xl"></div>
        <div className="container mx-auto px-4 relative">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">AiDA Products</h1>
          <p className="text-xl max-w-3xl">
            Advanced AI solutions that transform customer interactions and business operations.
          </p>
        </div>
      </div>
      
      <ContentSection 
        title="AiDA Digital Assistant Overview"
        content="AiDA, our AI Empowered Digital Assistant, represents a revolutionary approach to customer interaction for online businesses. Built on advanced Large Language Model (LLM) technology, AiDA delivers human-like, personalized, efficient, and secure customer experiences that transform how businesses engage with their audience.\n\nUnlike traditional customer service solutions that often leave users frustrated, AiDA reimagines online customer assistance by providing comprehensive knowledge, quick information retrieval, multi-role functionality, and genuine empathy in every interaction. This next-generation solution operates continuously, supports multiple languages, reduces operational costs, and maintains consistent quality standards across all customer touchpoints."
        imageUrl="/assets/images/aida-product.png"
      />
      
      <FeatureSection 
        title="Key Capabilities" 
        features={capabilitiesFeatures} 
      />
      
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Business Connector Architecture</h2>
          <div className="max-w-4xl mx-auto">
            <p className="text-lg text-gray-600 mb-8">
              At the heart of AiDA's functionality is our sophisticated Business Connector architecture, which seamlessly bridges customer needs with your business capabilities:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3 text-accent-600">Understanding Module</h3>
                <p className="text-gray-600">
                  This component analyzes customer queries through advanced query analysis and routing planning, ensuring each request is properly understood and directed.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3 text-secondary-600">Processing Engine</h3>
                <p className="text-gray-600">
                  Featuring graph-based agents, data extraction capabilities, retrieval-augmented generation, and specialized function tools for transforming inquiries into actionable responses.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3 text-primary-700">Integration Layer</h3>
                <p className="text-gray-600">
                  AiDA connects seamlessly with your business workflow, relational databases, vector databases, and web search capabilities for comprehensive information retrieval.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-semibold mb-3 text-accent-600">Response Construction</h3>
                <p className="text-gray-600">
                  Through sophisticated filtering and post-processing, AiDA delivers responses that are accurate, contextually appropriate, and aligned with your brand voice.
                </p>
              </div>
            </div>
            <p className="text-lg text-gray-600">
              This architecture ensures that AiDA serves as an effective interface between your customers and your business systems, providing value at every interaction point.
            </p>
          </div>
        </div>
      </div>
      
      <FeatureSection 
        title="Industry Applications" 
        features={industryFeatures} 
      />
      
      <div className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Experience AiDA?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contact us today to schedule a demonstration and discover how our AI Digital Assistant can transform your customer interactions.
          </p>
          <a 
            href="/contact" 
            className="inline-block bg-secondary-500 text-white hover:bg-secondary-600 px-8 py-3 rounded-md font-medium transition-colors shadow-lg"
          >
            Request a Demo
          </a>
        </div>
      </div>
    </>
  );
};

export default ProductsPage;
